import { CloseCircleOutlined, DownloadOutlined, InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, Button, List, Modal, Space, Table, Tag, Typography, Upload } from "antd";
import { useMemo, useState } from "react";
import { saveAs } from "file-saver";
import JwtAxios from "apiServices";
import { NEW_TNA_TEMPLATE_IMPORT_ENDPOINT, NEW_TNA_TEMPLATE_SAMPLE_DOWNLOAD_ENDPOINT } from "apiServices/API_ENDPOINTS";
import HandleNotification from "common/Notification";

const toMessage = (message, fallback) => {
  if (Array.isArray(message)) {
    return message[0] || fallback;
  }
  return message || fallback;
};

const ROW_ERROR_PATTERN = /^Row (\d+):\s*(.*)$/;

const groupErrors = (errors) => {
  const generalErrors = [];
  const issuesByRow = new Map();

  errors.forEach((error) => {
    const match = String(error).match(ROW_ERROR_PATTERN);
    if (!match) {
      generalErrors.push(error);
      return;
    }

    const row = Number(match[1]);
    const issues = match[2].split(";").map((issue) => issue.trim()).filter(Boolean);
    issuesByRow.set(row, [...(issuesByRow.get(row) || []), ...issues]);
  });

  const rowErrors = [...issuesByRow.entries()]
    .map(([row, issues]) => ({ row, issues }))
    .sort((a, b) => a.row - b.row);

  return { generalErrors, rowErrors };
};

const rowErrorColumns = [
  {
    title: "Excel Row",
    dataIndex: "row",
    key: "row",
    width: 110,
    render: (row) => <Tag color="red">Row {row}</Tag>,
  },
  {
    title: "Issues",
    dataIndex: "issues",
    key: "issues",
    render: (issues) => (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {issues.map((issue) => (
          <li key={issue}>
            <Typography.Text>{issue}</Typography.Text>
          </li>
        ))}
      </ul>
    ),
  },
];

const UploadErrors = ({ title, errors }) => {
  const { generalErrors, rowErrors } = useMemo(() => groupErrors(errors), [errors]);
  const issueCount = generalErrors.length + rowErrors.reduce((total, item) => total + item.issues.length, 0);

  const description =
    errors.length > 0
      ? `Found ${issueCount} issue(s)${rowErrors.length ? ` in ${rowErrors.length} row(s)` : ""}. Nothing was imported — please fix them in your Excel file and upload again.`
      : null;

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Alert
        type="error"
        showIcon
        message={errors.length > 0 ? "Upload failed" : title}
        description={description}
      />

      {generalErrors.length > 0 && (
        <List
          size="small"
          bordered
          header={<Typography.Text strong>File Issues</Typography.Text>}
          dataSource={generalErrors}
          renderItem={(item) => (
            <List.Item>
              <Space align="start">
                <CloseCircleOutlined style={{ color: "#ff4d4f", marginTop: 4 }} />
                <Typography.Text>{item}</Typography.Text>
              </Space>
            </List.Item>
          )}
        />
      )}

      {rowErrors.length > 0 && (
        <Table
          size="small"
          bordered
          rowKey="row"
          title={() => <Typography.Text strong>Row Issues</Typography.Text>}
          columns={rowErrorColumns}
          dataSource={rowErrors}
          pagination={false}
          scroll={{ y: 280 }}
        />
      )}
    </Space>
  );
};

const UploadTemplateModal = ({ open, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState([]);
  const [errorTitle, setErrorTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const resetState = () => {
    setFile(null);
    setErrors([]);
    setErrorTitle("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const downloadSample = async () => {
    setDownloading(true);
    try {
      const response = await JwtAxios.get(NEW_TNA_TEMPLATE_SAMPLE_DOWNLOAD_ENDPOINT, { responseType: "blob" });

      if (response.data?.type === "application/json") {
        const json = JSON.parse(await response.data.text());
        HandleNotification("error", "bottomRight", toMessage(json?.message, "Failed to download example file"), null);
        return;
      }

      saveAs(response.data, "tna-template-sample.xlsx");
    } catch (error) {
      HandleNotification("error", "bottomRight", "Failed to download example file", null);
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      HandleNotification("error", "bottomRight", "Please select an excel file", null);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setErrors([]);
    setErrorTitle("");

    try {
      const response = await JwtAxios.post(NEW_TNA_TEMPLATE_IMPORT_ENDPOINT, formData);
      const result = response?.data;

      if (result?.code === 200) {
        HandleNotification("success", "bottomRight", toMessage(result?.message, "TNA template imported successfully"), null);
        resetState();
        onUploaded();
        return;
      }

      setErrorTitle(toMessage(result?.message, "Upload failed"));
      setErrors(Array.isArray(result?.data) ? result.data : []);
    } catch (error) {
      setErrorTitle(toMessage(error?.response?.data?.message, "Upload failed. Please try again."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      title="Upload TNA Template"
      open={open}
      onCancel={handleClose}
      width={750}
      destroyOnClose
      footer={[
        <Button key="cancel" size="small" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          size="small"
          icon={<UploadOutlined />}
          loading={uploading}
          disabled={!file}
          onClick={handleUpload}
        >
          Upload
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Typography.Text type="secondary">
            Required columns: Title, Buyer, Buying Office, Task, Process, Days, Responsible Person
          </Typography.Text>
          <Button size="small" icon={<DownloadOutlined />} loading={downloading} onClick={downloadSample}>
            Download Example Excel
          </Button>
        </Space>

        <Upload.Dragger
          accept=".xlsx,.xls"
          maxCount={1}
          fileList={file ? [file] : []}
          beforeUpload={(selectedFile) => {
            setFile(selectedFile);
            setErrors([]);
            setErrorTitle("");
            return false;
          }}
          onRemove={() => setFile(null)}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag an excel file to this area</p>
        </Upload.Dragger>

        {errorTitle && <UploadErrors title={errorTitle} errors={errors} />}
      </Space>
    </Modal>
  );
};

export default UploadTemplateModal;
